import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AniListMedia, WatchlistEntry } from '../types';
import { useWatchlist } from '../composables/useWatchlist';
import { airingDayName, airingTimeHHMM, preferredTitle, useAniList } from '../composables/useAniList';

export const useWatchlistStore = defineStore('watchlist', () => {
  const entries = ref<WatchlistEntry[]>([]);
  const loading = ref(false);
  const wlApi = useWatchlist();
  const anilist = useAniList();

  const watching = computed(() => entries.value.filter((e) => e.status === 'watching'));
  const paused = computed(() => entries.value.filter((e) => e.status === 'paused'));

  async function load(): Promise<void> {
    loading.value = true;
    try {
      entries.value = await wlApi.list();
    } finally {
      loading.value = false;
    }
  }

  async function addFromMedia(media: AniListMedia): Promise<void> {
    const next =
      media.nextAiringEpisode ??
      media.airingSchedule.nodes[0] ??
      null;
    const entry: Omit<WatchlistEntry, 'id'> = {
      anilistId: media.id,
      malId: media.idMal ?? null,
      title: preferredTitle(media),
      titleJp: media.title.native ?? null,
      coverUrl: media.coverImage.large,
      status: 'watching',
      totalEpisodes: media.episodes,
      lastSeenEpisode: 0,
      airingDay: next ? airingDayName(next.airingAt) : undefined,
      airingTime: next ? airingTimeHHMM(next.airingAt) : undefined,
      nextEpisode: next?.episode ?? null,
      nextEpisodeAiringAt: next?.airingAt ?? null,
      addedAt: new Date().toISOString(),
    };
    await wlApi.add(entry);
    await load();
  }

  async function update(id: number, patch: Partial<WatchlistEntry>): Promise<void> {
    await wlApi.update(id, patch);
    await load();
  }

  async function remove(id: number): Promise<void> {
    await wlApi.remove(id);
    await load();
  }

  async function refreshAiringFromAniList(): Promise<void> {
    loading.value = true;
    try {
      const results = await Promise.allSettled(
        entries.value.map(async (entry) => {
          const media = await anilist.getDetails(entry.anilistId);
          if (!media || entry.id == null) return;
          const next =
            media.nextAiringEpisode ??
            media.airingSchedule.nodes[0] ??
            null;
          await wlApi.update(entry.id, {
            totalEpisodes: media.episodes,
            airingDay: next ? airingDayName(next.airingAt) : undefined,
            airingTime: next ? airingTimeHHMM(next.airingAt) : undefined,
            nextEpisode: next?.episode ?? null,
            nextEpisodeAiringAt: next?.airingAt ?? null,
          });
        }),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        // eslint-disable-next-line no-console
        console.warn(`Refresh: ${failed} entries failed`);
      }
      await load();
    } finally {
      loading.value = false;
    }
  }

  return {
    entries,
    watching,
    paused,
    loading,
    load,
    addFromMedia,
    update,
    remove,
    refreshAiringFromAniList,
    api: wlApi,
  };
});
